import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Lock, LogOut, Mail, MessageCircle, Phone, Plus, Search } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["NEW", "CONTACTED", "QUALIFIED", "MEETING", "CLOSED", "NOT SUITABLE"];
const STATUS_STYLES = {
  NEW: "bg-orange-rific/15 text-orange-rific",
  CONTACTED: "bg-deep/10 text-deep",
  QUALIFIED: "bg-fresh/15 text-fresh",
  MEETING: "bg-deep text-cream",
  CLOSED: "bg-cream-dark text-deep/60",
  "NOT SUITABLE": "bg-red-100 text-red-700",
};

const getToken = () => sessionStorage.getItem("rific_admin_token");
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso || "—";
  }
};

function Login() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "RIFIC Admin";
    const el = document.createElement("meta");
    el.setAttribute("name", "robots");
    el.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/admin/login", { password });
      sessionStorage.setItem("rific_admin_token", data.token);
      navigate("/admin/leads", { replace: true });
    } catch {
      setError("Incorrect password. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-deep px-5" data-testid="admin-login">
      <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-cream p-8" data-testid="admin-login-form">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-orange-rific">
          <Lock className="h-5 w-5 text-white" />
        </span>
        <h1 className="mt-5 text-center font-display text-2xl font-extrabold tracking-[-0.01em] text-deep">RIFIC Admin</h1>
        <p className="mt-1 text-center text-sm text-deep/55">Franchise leads — restricted access</p>
        <input
          data-testid="admin-password-input"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Admin password"
          className="mt-6 h-12 w-full rounded-xl border border-cream-line bg-white px-4 text-base text-deep outline-none focus:border-orange-rific focus:ring-2 focus:ring-orange-rific/25"
        />
        {error && (
          <p data-testid="admin-error" className="mt-3 text-sm font-semibold text-red-600">
            {error}
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-button"
          disabled={busy}
          className="mt-5 h-12 w-full rounded-full bg-orange-rific text-sm font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-deep disabled:opacity-60"
        >
          {busy ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function csvEscape(v) {
  let s;
  if (v === null || v === undefined) s = "";
  else if (typeof v === "boolean") s = v ? "Yes" : "No";
  else if (Array.isArray(v)) s = v.map((n) => `${(n.at || "").slice(0, 16).replace("T", " ")}: ${n.text || ""}`).join(" | ");
  else s = String(v);
  return `"${s.replace(/"/g, '""')}"`;
}

function exportCsv(leads) {
  const cols = [
    "lead_id", "created_at", "name", "mobile", "email", "city", "state",
    "preferred_location", "shop_area", "investment_range", "experience",
    "message", "consent", "status", "has_location", "franchise_model", "campaign", "notes",
  ];
  const lines = [cols.join(",")];
  leads.forEach((l) => lines.push(cols.map((c) => csvEscape(l[c])).join(",")));
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "rific-franchise-leads.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function useLeads() {
  const [leads, setLeads] = useState(null);
  const navigate = useNavigate();

  const reload = () => {
    const token = getToken();
    api
      .get("/admin/leads", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLeads(res.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          sessionStorage.removeItem("rific_admin_token");
          navigate("/admin", { replace: true });
        }
      });
  };

  useEffect(reload, []);
  return { leads, setLeads, reload };
}

function Dashboard() {
  const { leads, setLeads } = useLeads();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [state, setState] = useState("");
  const [investment, setInvestment] = useState("");
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    if (!leads) return null;
    const needle = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (needle && ![l.name, l.mobile, l.city].some((v) => (v || "").toLowerCase().includes(needle))) return false;
      if (status && l.status !== status) return false;
      if (state && (l.state || "") !== state) return false;
      if (investment && l.investment_range !== investment) return false;
      return true;
    });
  }, [leads, q, status, state, investment]);

  const stats = useMemo(() => {
    const s = { total: 0, NEW: 0, CONTACTED: 0, QUALIFIED: 0, MEETING: 0 };
    (leads || []).forEach((l) => {
      s.total += 1;
      if (s[l.status] !== undefined) s[l.status] += 1;
    });
    return s;
  }, [leads]);

  const states = useMemo(() => [...new Set((leads || []).map((l) => l.state).filter(Boolean))].sort(), [leads]);

  const logout = () => {
    sessionStorage.removeItem("rific_admin_token");
    navigate("/admin", { replace: true });
  };

  if (!leads) {
    return (
      <div className="min-h-screen bg-cream px-4 py-10 md:px-10">
        <p className="mx-auto max-w-[1440px] text-sm text-deep/50" data-testid="admin-loading">Loading leads…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream px-4 py-10 md:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1440px]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-[-0.01em] text-deep md:text-4xl">
            RIFIC Franchise Leads
          </h1>
          <div className="flex gap-3">
            <button
              data-testid="admin-export-button"
              onClick={() => exportCsv(leads)}
              className="inline-flex h-11 items-center gap-2 rounded-full bg-deep px-5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-orange-rific"
            >
              <Download className="h-4 w-4" /> Export Leads
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex h-11 items-center gap-2 rounded-full border border-deep/20 px-5 text-xs font-bold uppercase tracking-[0.14em] text-deep transition-colors hover:border-orange-rific hover:text-orange-rific"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="admin-stats">
          {[
            ["Total Leads", stats.total, "admin-stat-total"],
            ["New", stats.NEW, "admin-stat-new"],
            ["Contacted", stats.CONTACTED, "admin-stat-contacted"],
            ["Qualified", stats.QUALIFIED, "admin-stat-qualified"],
            ["Meetings", stats.MEETING, "admin-stat-meetings"],
          ].map(([t, v, tid]) => (
            <div key={tid} className="rounded-2xl border border-cream-line bg-white p-4 md:p-5" data-testid={tid}>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-deep/50">{t}</p>
              <p className="mt-1 font-display text-2xl font-extrabold text-deep md:text-3xl">{v}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-4" data-testid="admin-filters">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-deep/40" />
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, mobile, city"
              className="h-11 w-full rounded-full border border-cream-line bg-white pl-10 pr-4 text-sm text-deep outline-none focus:border-orange-rific"
            />
          </div>
          <select data-testid="admin-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 rounded-full border border-cream-line bg-white px-4 text-sm font-medium text-deep outline-none focus:border-orange-rific">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="admin-filter-state" value={state} onChange={(e) => setState(e.target.value)} className="h-11 rounded-full border border-cream-line bg-white px-4 text-sm font-medium text-deep outline-none focus:border-orange-rific">
            <option value="">All states</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="admin-filter-investment" value={investment} onChange={(e) => setInvestment(e.target.value)} className="h-11 rounded-full border border-cream-line bg-white px-4 text-sm font-medium text-deep outline-none focus:border-orange-rific">
            <option value="">All investment ranges</option>
            {["Up to ₹5 lakh", "₹5–8 lakh", "₹8–10 lakh", "₹10 lakh+", "Need Guidance"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-cream-line bg-white" data-testid="admin-leads-table">
          <table className="w-full min-w-[1000px] text-left">
            <thead>
              <tr className="border-b border-cream-line bg-cream/60">
                {["Date", "Name", "Mobile", "City", "State", "Investment Range", "Shop Area", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-deep/60">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(filtered || []).map((l) => (
                <tr
                  key={l.id}
                  data-testid={`admin-lead-row-${l.id}`}
                  onClick={() => navigate(`/admin/leads/${l.id}`)}
                  className="cursor-pointer border-b border-cream-line/60 transition-colors hover:bg-cream/40"
                >
                  <td className="whitespace-nowrap px-4 py-4 text-xs text-deep/60">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-deep">{l.name}</td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-deep/80">{l.mobile}</td>
                  <td className="px-4 py-4 text-sm text-deep/80">{l.city}</td>
                  <td className="px-4 py-4 text-sm text-deep/80">{l.state || "—"}</td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm text-deep/80">{l.investment_range || "—"}</td>
                  <td className="px-4 py-4 text-sm text-deep/80">{l.shop_area || "—"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[l.status] || "bg-cream-dark text-deep"}`} data-testid={`admin-lead-status-${l.id}`}>
                      {l.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-deep/55" data-testid="admin-empty">
                    No leads match your search or filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-deep/45" data-testid="admin-result-count">
          Showing {(filtered || []).length} of {leads.length} leads · newest first
        </p>
      </div>
    </div>
  );
}

function LeadDetail({ id }) {
  const { leads, setLeads } = useLeads();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const lead = (leads || []).find((l) => l.id === id);
  const navigate = useNavigate();

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setLeads((ls) => (ls || []).map((l) => (l.id === id ? data : l)));
    } finally {
      setSaving(false);
    }
  };

  if (!leads) return <div className="min-h-screen bg-cream px-5 py-10 text-sm text-deep/50">Loading…</div>;

  if (!lead) {
    return (
      <div className="min-h-screen bg-cream px-5 py-10" data-testid="admin-lead-not-found">
        <p className="text-sm font-semibold text-deep">Lead not found.</p>
        <Link to="/admin/leads" data-testid="admin-back-button" className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-orange-rific">
          <ArrowLeft className="h-4 w-4" /> Back to leads
        </Link>
      </div>
    );
  }

  const waNumber = `91${lead.mobile.replace(/\D/g, "").slice(-10)}`;

  return (
    <div className="min-h-screen bg-cream px-4 py-10 md:px-10" data-testid="admin-lead-detail">
      <div className="mx-auto max-w-4xl">
        <Link to="/admin/leads" data-testid="admin-back-button" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-deep/60 transition-colors hover:text-orange-rific">
          <ArrowLeft className="h-4 w-4" /> All leads
        </Link>

        <div className="mt-5 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-[-0.01em] text-deep" data-testid="admin-lead-name">{lead.name}</h1>
            <p className="mt-1 text-sm text-deep/55" data-testid="admin-lead-submitted">Submitted {fmtDate(lead.created_at)}</p>
          </div>
          <span className={`rounded-full px-4 py-2 text-xs font-bold ${STATUS_STYLES[lead.status] || "bg-cream-dark text-deep"}`} data-testid="admin-lead-status-badge">
            {lead.status}
          </span>
        </div>

        <div className="mt-6 flex flex-wrap gap-3" data-testid="admin-quick-actions">
          <a href={`tel:${lead.mobile.replace(/\s/g, "")}`} data-testid="admin-action-call" className="inline-flex h-11 items-center gap-2 rounded-full bg-deep px-5 text-xs font-bold uppercase tracking-[0.14em] text-cream transition-colors hover:bg-orange-rific">
            <Phone className="h-4 w-4" /> Call
          </a>
          <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-testid="admin-action-whatsapp" className="inline-flex h-11 items-center gap-2 rounded-full bg-fresh px-5 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-deep">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          {lead.email && (
            <a href={`mailto:${lead.email}`} data-testid="admin-action-email" className="inline-flex h-11 items-center gap-2 rounded-full border border-deep/20 px-5 text-xs font-bold uppercase tracking-[0.14em] text-deep transition-colors hover:border-orange-rific hover:text-orange-rific">
              <Mail className="h-4 w-4" /> Email
            </a>
          )}
        </div>

        <div className="mt-6 rounded-2xl border border-cream-line bg-white p-6" data-testid="admin-lead-info">
          <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {[
              ["Mobile", lead.mobile],
              ["Email", lead.email || "—"],
              ["City", lead.city],
              ["State", lead.state || "—"],
              ["Preferred Location", lead.preferred_location || "—"],
              ["Available Shop Area", lead.shop_area || "—"],
              ["Investment Range", lead.investment_range || "—"],
              ["Business Experience", lead.experience || "—"],
              ["Do you have a location?", lead.has_location || "—"],
              ["Franchise Model", lead.franchise_model || "—"],
              ["Campaign", lead.campaign || "—"],
              ["Consent", lead.consent ? "Yes" : "No"],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-deep/45">{k}</p>
                <p className="mt-1 text-sm font-medium text-deep" data-testid={`admin-lead-field-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
              </div>
            ))}
          </div>
          {lead.message && (
            <div className="mt-5 border-t border-cream-line pt-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-deep/45">Message</p>
              <p className="mt-1 text-sm leading-relaxed text-deep/80">{lead.message}</p>
            </div>
          )}
        </div>

        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-cream-line bg-white p-6" data-testid="admin-status-panel">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-deep/45">Lead Status</p>
            <select
              data-testid={`admin-status-select-${lead.id}`}
              value={lead.status}
              disabled={saving}
              onChange={(e) => patch({ status: e.target.value })}
              className={`mt-3 h-11 w-full rounded-xl px-4 text-sm font-bold outline-none ${STATUS_STYLES[lead.status] || "bg-cream-dark text-deep"}`}
            >
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="rounded-2xl border border-cream-line bg-white p-6" data-testid="admin-notes-panel">
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-deep/45">Internal Notes</p>
            <div className="mt-3 flex gap-2">
              <input
                data-testid="admin-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add an internal note"
                className="h-11 w-full rounded-xl border border-cream-line bg-cream/50 px-4 text-sm text-deep outline-none focus:border-orange-rific"
              />
              <button
                data-testid="admin-add-note-button"
                disabled={saving || !note.trim()}
                onClick={async () => { await patch({ note: note.trim() }); setNote(""); }}
                className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-orange-rific px-4 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-deep disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Add
              </button>
            </div>
            <ul className="mt-4 space-y-2" data-testid="admin-notes-list">
              {(lead.notes || []).length === 0 && <li className="text-xs text-deep/40">No notes yet.</li>}
              {(lead.notes || []).slice().reverse().map((n, i) => (
                <li key={i} className="rounded-xl bg-cream/60 px-4 py-3">
                  <p className="text-sm text-deep">{n.text}</p>
                  <p className="mt-1 font-mono text-[10px] text-deep/40">{fmtDate(n.at)}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { pathname } = useLocation();
  const token = getToken();

  if (!token) {
    return pathname === "/admin" ? <Login /> : <Navigate to="/admin" replace />;
  }
  if (pathname === "/admin") return <Navigate to="/admin/leads" replace />;
  if (pathname === "/admin/leads") return <Dashboard />;
  const match = pathname.match(/^\/admin\/leads\/([^/]+)$/);
  if (match) return <LeadDetail id={match[1]} />;
  return <Navigate to="/admin/leads" replace />;
}
